import React, { useState, useEffect, useRef } from 'react';
import Header from '../../partials/Header';
import Sidebar from '../../partials/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import ComplianceConstellation from '../../components/ComplianceConstellation';
import axios from 'axios';
import { Config } from '../../config';
import { normalizeRiskLevel, riskLevelLabel } from '../../utils/riskLevel';

// Add CSS for fade-in animation
const fadeInStyle = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.6s ease-out forwards;
    opacity: 0;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = fadeInStyle;
  document.head.appendChild(styleSheet);
}

function Discovery() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [dots, setDots] = useState(1);
  
  const [organizationData, setOrganizationData] = useState({
    name: '',
    website: '',
    country: '',
    sector: ''
  });

  const [discoveryPreferences, setDiscoveryPreferences] = useState({
    includePotentialRequirements: true,
    focusOnHighRisk: true,
    includeEmergingRegulations: false
  });

  const [dataCriteria, setDataCriteria] = useState({
    dataTypes: [],
    hasInternationalOperations: false,
    publiclyTraded: false,
    handlesPayments: false,
    isCloudServiceProvider: false,
    criticalInfrastructure: false
  });

  const dataTypes = [
    'Personal Data (PII)', 'Payment Card Data', 'Health Information (PHI)', 'Financial Data',
    'Biometric Data', 'Location Data', 'Marketing Data', 'Employee Data',
    'Customer Data', 'Intellectual Property', 'Trade Secrets', 'Social Security Numbers',
    'Driver License Data', 'Passport Information', 'Credit Reports', 'Bank Account Details'
  ];

  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
    'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
    'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
    'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
    'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
    'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
    'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany',
    'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
    'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
    'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
    'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
    'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
    'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
    'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
    'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
    'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
    'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
    'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
    'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago',
    'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates',
    'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
    'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  const sectorOptions = [
    {
      category: "Financial Services",
      options: [
        "Banking",
        "Securities & Capital Markets", 
        "Investment Services",
        "Payment Services",
        "Insurance",
        "Cryptocurrency & Digital Assets",
        "Financial Services (General)"
      ]
    },
    {
      category: "Technology",
      options: [
        "Cybersecurity",
        "Information Security",
        "Telecommunications",
        "Internet Service Providers",
        "Software Development",
        "Cloud Services",
        "Technology (General)"
      ]
    },
    {
      category: "Healthcare",
      options: [
        "Healthcare Providers",
        "Health Insurance",
        "Medical Devices",
        "Pharmaceuticals",
        "Healthcare (General)"
      ]
    },
    {
      category: "Government & Public Sector",
      options: [
        "Federal Government",
        "State/Regional Government",
        "Local Government",
        "Critical Infrastructure",
        "Defense & Military",
        "Government (General)"
      ]
    },
    {
      category: "Other Industries",
      options: [
        "Education",
        "Retail & E-commerce",
        "Manufacturing",
        "Energy & Utilities",
        "Transportation",
        "Hospitality",
        "Other"
      ]
    }
  ];

  const phases = [
    'Scanning Regulations',
    'Analyzing Context',
    'Mapping Requirements'
  ];

  // Handle phase cycling and dots animation during analysis
  useEffect(() => {
    if (!isAnalyzing) {
      setCurrentPhase(0);
      setDots(1);
      return;
    }

    // Cycle through phases every 4 seconds
    const phaseInterval = setInterval(() => {
      setCurrentPhase(prev => (prev + 1) % phases.length);
    }, 4000);

    // Animate dots every 600ms
    const dotsInterval = setInterval(() => {
      setDots(prev => (prev % 3) + 1);
    }, 600);

    return () => {
      clearInterval(phaseInterval);
      clearInterval(dotsInterval);
    };
  }, [isAnalyzing, phases.length]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${Config.APIBaseURL}/user/me`, {
          headers: {
            'access_token': localStorage.getItem('token')
          }
        });
        
        const userData = response.data;
        let orgDetails = null;

        // Try to fetch organization details if we have an org ID
        if (userData.user_info?.org) {
          try {
            const orgResponse = await axios.get(`${Config.APIBaseURL}/customers/`, {
              headers: {
                'access_token': localStorage.getItem('token')
              }
            });
            
            orgDetails = orgResponse.data.find(org => org.id === userData.user_info.org);
          } catch (orgError) {
            console.error('Error fetching organization details:', orgError);
          }
        }

        // Pre-populate organization data with available information
        setOrganizationData({
          name: orgDetails?.name || 
                orgDetails?.company_name || 
                userData.user_info?.org_name || 
                userData.user_info?.company || 
                userData.user_info?.organization || 
                '',
          website: orgDetails?.website || 
                   orgDetails?.company_website || 
                   userData.user_info?.website || 
                   userData.user_info?.company_website || 
                   '',
          country: orgDetails?.country || 
                   userData.user_info?.country || 
                   userData.user_info?.location?.country || 
                   '',
          sector: orgDetails?.sector || 
                  orgDetails?.industry || 
                  userData.user_info?.sector || 
                  userData.user_info?.industry || 
                  ''
        });



      } catch (error) {
        console.error('Error fetching user data:', error);
        
        // Fallback: Try to get data from localStorage as last resort
        try {
          const userInfo = JSON.parse(localStorage.getItem('userinfo') || '{}');
          const fallbackData = {
            name: userInfo?.org_name || userInfo?.company || userInfo?.organization || '',
            website: userInfo?.website || userInfo?.company_website || '',
            country: userInfo?.country || '',
            sector: userInfo?.sector || userInfo?.industry || ''
          };
          
          // Only use fallback data if we have at least some information
          if (fallbackData.name || fallbackData.country) {
            setOrganizationData(fallbackData);
          } else {
            // No data available, set empty fields for manual entry
            setOrganizationData({
              name: '',
              website: '',
              country: '',
              sector: ''
            });
          }
        } catch (fallbackError) {
          console.error('Error accessing localStorage fallback:', fallbackError);
          // Complete fallback - empty fields
          setOrganizationData({
            name: '',
            website: '',
            country: '',
            sector: ''
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Add ref for constellation
  const constellationRef = useRef(null);

  const handleStartDiscovery = async () => {
    setIsAnalyzing(true);
    
    // Scroll to constellation immediately when starting
    if (constellationRef.current) {
      constellationRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
    
    try {
      const discoveryPayload = {
        organization: organizationData,
        preferences: discoveryPreferences,
        data_criteria: dataCriteria
      };

      const response = await axios.post(`${Config.APIBaseURL}/grc/discovery/analyze`, discoveryPayload, {
        headers: {
          'access_token': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      setAnalysisResults(response.data);
    } catch (error) {
      console.error('Error running discovery analysis:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSaveOrgChanges = () => {
    setIsEditingOrg(false);
    // TODO: Save changes to backend
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/* Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            
            {/* Page header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    Compliance Discovery
                  </h1>
                  <p className="text-slate-600 dark:text-slate-400 mt-2">
                    AI-powered regulatory requirement discovery for {organizationData?.name || 'your organization'}
                  </p>
                </div>
              </div>
            </div>

            {/* Compliance Constellation */}
            <div ref={constellationRef} className="mb-8">
              <ComplianceConstellation isAnalyzing={isAnalyzing} />
            </div>

            {/* Analysis Status - Show when analyzing */}
            {isAnalyzing && (
              <div className="mb-8 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 text-center">
                <div className="max-w-lg mx-auto">
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full animate-pulse"></div>
                    <div className="flex items-center">
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                        {phases[currentPhase]}
                      </h3>
                      <span className="text-xl font-bold text-slate-800 dark:text-slate-100 w-8 text-left">
                        {'.'.repeat(dots)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!analysisResults ? (
              <div className="w-full">
                {/* Single Unified Discovery Panel */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
                  <div className="p-8">
                    <div className="max-w-7xl mx-auto">
                    
                      {/* Main Content Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-8">
                        
                        {/* Left Column - Organization Profile */}
                        <div className="lg:col-span-1">
                          <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center">
                              <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2-16V3a2 2 0 012-2h10a2 2 0 012 2v2M7 8h10M7 12h10M7 16h10" />
                              </svg>
                              Organization Profile
                            </h2>
                            <button
                              onClick={() => setIsEditingOrg(!isEditingOrg)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-slate-700 border border-indigo-200 dark:border-indigo-500/30 rounded-md hover:bg-indigo-100 dark:hover:bg-slate-600 transition-colors duration-200"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              {isEditingOrg ? 'Save' : 'Edit'}
                            </button>
                          </div>
                          
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                Organization Name
                              </label>
                              {isEditingOrg ? (
                                <input
                                  type="text"
                                  value={organizationData.name}
                                  onChange={(e) => setOrganizationData({...organizationData, name: e.target.value})}
                                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                />
                              ) : (
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-200 font-medium text-sm">
                                  {organizationData.name || 'Not specified'}
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                Website URL
                              </label>
                              {isEditingOrg ? (
                                <input
                                  type="url"
                                  value={organizationData.website}
                                  onChange={(e) => setOrganizationData({...organizationData, website: e.target.value})}
                                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                />
                              ) : (
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-200 font-medium text-sm">
                                  {organizationData.website || 'Not specified'}
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                Country
                              </label>
                              {isEditingOrg ? (
                                <select
                                  value={organizationData.country}
                                  onChange={(e) => setOrganizationData({...organizationData, country: e.target.value})}
                                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                >
                                  <option value="">Select a country</option>
                                  {countries.map(country => (
                                    <option key={country} value={country}>{country}</option>
                                  ))}
                                </select>
                              ) : (
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-200 font-medium text-sm">
                                  {organizationData.country || 'Not specified'}
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                Industry Sector
                              </label>
                              {isEditingOrg ? (
                                <select
                                  value={organizationData.sector}
                                  onChange={(e) => setOrganizationData({...organizationData, sector: e.target.value})}
                                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                >
                                  <option value="">Select a sector</option>
                                  {sectorOptions.map(category => (
                                    <optgroup key={category.category} label={category.category}>
                                      {category.options.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                              ) : (
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-200 font-medium text-sm">
                                  {organizationData.sector || 'Not specified'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Middle Column - Analysis Preferences & Data Types */}
                        <div className="lg:col-span-1">
                          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-6 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Analysis Preferences
                          </h2>

                          <div className="space-y-3 mb-6">
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={discoveryPreferences.includePotentialRequirements}
                                onChange={(e) => setDiscoveryPreferences({...discoveryPreferences, includePotentialRequirements: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">Potential Requirements</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Include future applicable requirements</div>
                              </div>
                            </label>
                            
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={discoveryPreferences.focusOnHighRisk}
                                onChange={(e) => setDiscoveryPreferences({...discoveryPreferences, focusOnHighRisk: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">High-Risk Focus</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Prioritize high compliance risk areas</div>
                              </div>
                            </label>
                            
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={discoveryPreferences.includeEmergingRegulations}
                                onChange={(e) => setDiscoveryPreferences({...discoveryPreferences, includeEmergingRegulations: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">Emerging Regulations</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Consider upcoming regulations</div>
                              </div>
                            </label>
                          </div>

                          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-4">Data Types Handled</h3>
                          <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                            {dataTypes.slice(0, 12).map(dataType => (
                              <label key={dataType} className="flex items-center space-x-2 p-2 border border-slate-200 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={dataCriteria.dataTypes.includes(dataType)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setDataCriteria({...dataCriteria, dataTypes: [...dataCriteria.dataTypes, dataType]});
                                    } else {
                                      setDataCriteria({...dataCriteria, dataTypes: dataCriteria.dataTypes.filter(t => t !== dataType)});
                                    }
                                  }}
                                  className="w-3 h-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{dataType}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Right Column - Business Characteristics & Action */}
                        <div className="lg:col-span-1">
                          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-6 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Business Profile
                          </h3>
                          
                          <div className="space-y-3 mb-8">
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dataCriteria.hasInternationalOperations}
                                onChange={(e) => setDataCriteria({...dataCriteria, hasInternationalOperations: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">International Operations</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Multi-country business operations</div>
                              </div>
                            </label>
                            
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dataCriteria.publiclyTraded}
                                onChange={(e) => setDataCriteria({...dataCriteria, publiclyTraded: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">Publicly Traded</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Listed on stock exchanges</div>
                              </div>
                            </label>
                            
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dataCriteria.handlesPayments}
                                onChange={(e) => setDataCriteria({...dataCriteria, handlesPayments: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">Payment Processing</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Credit card or financial data</div>
                              </div>
                            </label>
                            
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dataCriteria.isCloudServiceProvider}
                                onChange={(e) => setDataCriteria({...dataCriteria, isCloudServiceProvider: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">Cloud Service Provider</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Provides cloud computing services</div>
                              </div>
                            </label>
                            
                            <label className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dataCriteria.criticalInfrastructure}
                                onChange={(e) => setDataCriteria({...dataCriteria, criticalInfrastructure: e.target.checked})}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                              />
                              <div>
                                <div className="font-medium text-slate-700 dark:text-slate-300 text-sm">Critical Infrastructure</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">Essential services (energy, health, finance)</div>
                              </div>
                            </label>
                          </div>

                          {/* Analysis Action */}
                          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                            <div className="flex items-center space-x-3 mb-4">
                              <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div>
                                <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">Ready to Analyze</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">500+ regulations across 50+ jurisdictions</p>
                              </div>
                            </div>
                            
                            {!isAnalyzing ? (
                              <button
                                onClick={handleStartDiscovery}
                                className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 text-sm"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Run Compliance Discovery
                              </button>
                            ) : (
                              <div className="text-center py-3">
                                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg">
                                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                                  <p className="font-medium text-indigo-800 dark:text-indigo-200 text-sm">Processing Analysis</p>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Watch the constellation above</p>
                              </div>
                            )}
                            
                            {/* Technical Summary */}
                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                              <details className="group">
                                <summary className="cursor-pointer text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center">
                                  <svg className="w-3 h-3 mr-1 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  Analysis Parameters
                                </summary>
                                <div className="mt-2 text-xs text-slate-600 dark:text-slate-400 pl-4 space-y-1">
                                  <p><span className="font-medium">Org:</span> {organizationData.name || 'Not specified'}</p>
                                  <p><span className="font-medium">Country:</span> {organizationData.country || 'Not specified'}</p>
                                  <p><span className="font-medium">Sector:</span> {organizationData.sector || 'Not specified'}</p>
                                  <p><span className="font-medium">Data Types:</span> {dataCriteria.dataTypes.length} selected</p>
                                  <p><span className="font-medium">Prefs:</span> {Object.values(discoveryPreferences).filter(Boolean).length} enabled</p>
                                </div>
                              </details>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Analysis Results */
              <div className="space-y-6">
                {/* Results Header */}
                <div className="bg-gradient-to-r from-green-50 to-indigo-50 dark:from-green-900/20 dark:to-indigo-900/20 rounded-xl border border-green-200 dark:border-green-800 p-6 animate-fadeIn">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2 flex items-center">
                        <svg className="w-8 h-8 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Discovery Complete
                      </h2>
                      <p className="text-slate-600 dark:text-slate-400">
                        Compliance analysis for <span className="font-semibold text-slate-800 dark:text-slate-200">{organizationData?.name}</span>
                      </p>
                      {analysisResults?.analysis_metadata && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          Analysis completed on {new Date(analysisResults.analysis_metadata.timestamp).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setAnalysisResults(null);
                        setIsAnalyzing(false);
                      }}
                      className="px-4 py-2 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                    >
                      Run New Discovery
                    </button>
                  </div>
                </div>

                {/* Compliance Summary */}
                {analysisResults?.compliance_summary && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 animate-fadeIn" style={{animationDelay: '0.1s'}}>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 00-2-2m0 0V9a2 2 0 012-2h2a2 2 0 00-2-2" />
                      </svg>
                      Compliance Overview
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{analysisResults.compliance_summary.total_regulations}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Total Regulations</div>
                      </div>
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">{analysisResults.compliance_summary.mandatory_count}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Mandatory</div>
                      </div>
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{analysisResults.compliance_summary.high_risk_count}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">High Risk</div>
                      </div>
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{analysisResults.compliance_summary.estimated_effort}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Effort Level</div>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Implementation Timeline:</strong> {analysisResults.compliance_summary.implementation_timeline}
                      </p>
                    </div>
                  </div>
                )}

                {/* Primary Regulations */}
                {analysisResults?.primary_regulations && analysisResults.primary_regulations.length > 0 && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 animate-fadeIn" style={{animationDelay: '0.2s'}}>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Primary Regulations
                    </h3>
                    <div className="space-y-4">
                      {analysisResults.primary_regulations.map((regulation, index) => (
                        <div key={index} className="border border-slate-200 dark:border-slate-600 rounded-lg p-4 animate-fadeIn" style={{animationDelay: `${0.3 + index * 0.1}s`}}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-bold text-slate-800 dark:text-slate-100">{regulation.name}</h4>
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                {regulation.acronym} • {regulation.geographic_scope} • {regulation.applicability}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                ['high', 'critical'].includes(normalizeRiskLevel(regulation.risk_level))
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                  : normalizeRiskLevel(regulation.risk_level) === 'medium'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                                  : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                              }`}>
                                {riskLevelLabel(regulation.risk_level)} Risk
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200">
                                {regulation.confidence}% confidence
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{regulation.reasoning}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">KEY REQUIREMENTS</p>
                              <div className="flex flex-wrap gap-1">
                                {regulation.key_requirements?.map((req, i) => (
                                  <span key={i} className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-xs">
                                    {req}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">DATA TYPES</p>
                              <div className="flex flex-wrap gap-1">
                                {regulation.data_types_covered?.map((type, i) => (
                                  <span key={i} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          {regulation.penalties && (
                            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                              <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1">PENALTIES</p>
                              <p className="text-sm text-red-800 dark:text-red-200">{regulation.penalties}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Frameworks */}
                {analysisResults?.frameworks && analysisResults.frameworks.length > 0 && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 animate-fadeIn" style={{animationDelay: '0.4s'}}>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Recommended Frameworks
                    </h3>
                    <div className="space-y-3">
                      {analysisResults.frameworks.map((framework, index) => (
                        <div key={index} className="border border-slate-200 dark:border-slate-600 rounded-lg p-4 animate-fadeIn" style={{animationDelay: `${0.5 + index * 0.1}s`}}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-slate-800 dark:text-slate-100">{framework.name}</h4>
                              <p className="text-sm text-slate-600 dark:text-slate-400">{framework.type} • {framework.applicability}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                framework.implementation_priority === 'High' 
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                  : framework.implementation_priority === 'Medium'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                                  : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                              }`}>
                                {framework.implementation_priority} Priority
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200">
                                {framework.confidence}% confidence
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{framework.reasoning}</p>
                          {framework.key_controls && (
                            <div>
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">KEY CONTROLS</p>
                              <div className="flex flex-wrap gap-1">
                                {framework.key_controls.map((control, i) => (
                                  <span key={i} className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs">
                                    {control}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Steps */}
                {analysisResults?.compliance_summary?.next_steps && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 animate-fadeIn" style={{animationDelay: '0.6s'}}>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Next Steps
                    </h3>
                    <div className="space-y-2">
                      {analysisResults.compliance_summary.next_steps.map((step, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg animate-fadeIn" style={{animationDelay: `${0.7 + index * 0.1}s`}}>
                          <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                            {index + 1}
                          </div>
                          <p className="text-slate-800 dark:text-slate-200">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {analysisResults?.recommendations && analysisResults.recommendations.length > 0 && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 animate-fadeIn" style={{animationDelay: '0.8s'}}>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Recommendations
                    </h3>
                    <div className="space-y-3">
                      {analysisResults.recommendations.map((recommendation, index) => (
                        <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg animate-fadeIn" style={{animationDelay: `${0.9 + index * 0.1}s`}}>
                          <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <p className="text-slate-800 dark:text-slate-200">{recommendation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Discovery; 